import { useEffect, useState } from 'react';
import { useConfig } from '../../providers/config-provider';
import { ILogoutOptions, useUserProfile } from '../../providers/user-provider';
import { AuthService, LoginDto } from '../../services/auth';
import { useRouter } from './useRouter';
import { InternalLinks } from '../enums/common';
import { getLoginURL, getLogoutURL } from '../../utils/configUtils';
import useServiceClients from './useServiceClients';
import { setSession, clearSession, clearPersonalizedAlertStorage, setPlpResetAfterLogin } from '../../utils/sessionStorageUtils';
import { isLocalEnv } from '../functions/isLocalEnv';
import useBenefitStore from './useBenefitStore';
import { removeCookie } from '../../utils/cookieUtils';
import { useAuthStore } from './useAuthStore';
import { useFavoriteStore } from './useFavoriteStore';
import useSplunkLogger from '../hooks/useSplunkLogger';

export const useAuthentication = () => {
  const { visionBffClient, visionBffMocksClient } = useServiceClients();
  const {
    userProfile,
    setUserProfile,
    setRxModalShown,
    setLiveSessionData,
    setLogoutTimer,
    resetNotificationState,
    setIsLoggingOut,
  } = useUserProfile();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const { navigate } = useRouter();
  const { config } = useConfig();
  const { splunkLogger } = useSplunkLogger();

  const handleLogin = async (loginDto: LoginDto): Promise<void> => {
    setLoading(true);
    const data = await AuthService.login(visionBffMocksClient, loginDto, splunkLogger);
    if (data?.error && error != '') {
      setLoading(false);
      return setError(data.error);
    }
    setSession('isLoggedIn', 'true');
    setPlpResetAfterLogin();
    navigate(InternalLinks.secureHome);
  };

  const loginRoute = () => {
    const loginURL = getLoginURL(config);
    setPlpResetAfterLogin();
    if (isLocalEnv()) {
      navigate(loginURL);
    } else {
      redirectToTopWindow(loginURL);
    }
  };

  // Ensure redirects navigate the top window
  const redirectToTopWindow = (url: string) => {
    try {
      const topWin = window.top as Window | null;
      if (topWin && topWin !== window) {
        topWin.location.replace(url);
      } else {
        window.location.replace(url);
      }
    } catch (_err) {
      // Cross-origin access to window.top may throw; fallback to current window
      window.location.replace(url);
    }
  };

  const kpdportalbff = config?.services?.kpdportalbff;
  const authStore = useAuthStore();
  const favStore = useFavoriteStore();

  const handleLogout = async (options?: ILogoutOptions): Promise<void> => {
    setLogoutTimer(false);
    setIsLoggingOut(true);
    await AuthService.logout(isLocalEnv() ? visionBffMocksClient : visionBffClient, splunkLogger);
    const logout = getLogoutURL(config);
    setSession('isLoggedIn', 'false');
    authStore.setUserProfile(null);
    favStore.setUserFavorites(null);
    setUserProfile(null);
    setLiveSessionData(null);
    resetNotificationState?.();
    clearPersonalizedAlertStorage();
    clearSession();
    useBenefitStore.getState().clearSelectedBenefit();
    const shouldRedirect = !options?.skipRedirect;
    if (shouldRedirect) setError('');
    setRxModalShown(() => {
      localStorage.removeItem('hasRxModalBeenShown');
      return false;
    });
    if (isLocalEnv()) {
      removeCookie();
      if (shouldRedirect) navigate(logout);
    } else if (shouldRedirect) {
      redirectToTopWindow(logout);
    }
  };

  useEffect(() => {
    if (isLocalEnv() && userProfile && location?.pathname?.split('/')?.includes('login')) {
      navigate(InternalLinks.secureHome, undefined, { isLoggedIn: true });
    }
  }, [userProfile]);

  return {
    loginRoute,
    handleLogin,
    handleLogout,
    loading,
    error,
    setError,
  };
};
